// frontend/src/services/reinsurance.js

import { apiService } from './api';
import { API_ENDPOINTS } from '../config/constants';
import { MOCK_ENABLED } from './mockConfig';
import { reinsuranceMockService } from './reinsuranceMock';

export const reinsuranceService = {
  // Dashboard data
  getDashboardData: async () => {
    if (MOCK_ENABLED) {
      return reinsuranceMockService.getDashboardData();
    }
    
    console.group('Getting Reinsurance Dashboard Data');
    
    try {
      const response = await apiService.get(API_ENDPOINTS.REINSURANCE.DASHBOARD_DATA);
      
      console.log('Dashboard data retrieved successfully:', response);
      console.groupEnd();
      return response;
    } catch (error) {
      console.error('Error getting dashboard data:', error);
      console.groupEnd();
      throw error;
    }
  },

  // Contract Management
  listContracts: async (filters = {}) => {
    if (MOCK_ENABLED) {
      return reinsuranceMockService.listContracts(filters);
    }
    
    console.group('Listing Reinsurance Contracts');
    console.log('Filters:', filters);
    
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      if (filters.status) queryParams.append('current_status', filters.status);
      if (filters.contractType) queryParams.append('contract_type', filters.contractType);
      
      const endpoint = `${API_ENDPOINTS.REINSURANCE.LIST_CONTRACTS}?${queryParams.toString()}`;
      const response = await apiService.get(endpoint);
      
      console.log('Contracts retrieved successfully:', response);
      console.groupEnd();
      return response;
    } catch (error) {
      console.error('Error listing contracts:', error);
      
      // Enhanced error handling
      if (error.response) {
        error.statusCode = error.response.status;
      }
      
      console.groupEnd();
      throw error;
    }
  },

  getContract: async (contractId) => {
    if (MOCK_ENABLED) {
      return reinsuranceMockService.getContract(contractId);
    }
    
    console.group('Getting Reinsurance Contract');
    console.log('Contract ID:', contractId);
    
    try {
      if (!contractId) {
        throw new Error('Contract ID is required');
      }
      
      const response = await apiService.get(`${API_ENDPOINTS.REINSURANCE.GET_CONTRACT}/${contractId}`);
      
      console.log('Contract retrieved successfully:', response);
      console.groupEnd();
      return response;
    } catch (error) {
      console.error('Error getting contract:', error);
      
      // Enhanced error handling
      if (error.response) {
        error.statusCode = error.response.status;
        
        // Special handling for 404 Not Found
        if (error.response.status === 404) {
          error.isContractNotFoundError = true;
          console.log('Contract not found (404 Not Found)');
        }
      }
      
      console.groupEnd();
      throw error;
    }
  },

  createContract: async (contractData) => {
    if (MOCK_ENABLED) {
      return reinsuranceMockService.createContract(contractData);
    }
    
    console.group('Creating Reinsurance Contract');
    console.log('Contract Data:', contractData);
    
    try {
      const response = await apiService.post(API_ENDPOINTS.REINSURANCE.CREATE_CONTRACT, contractData);
      
      console.log('Contract created successfully:', response);
      console.groupEnd();
      return response;
    } catch (error) {
      console.error('Error creating contract:', error);
      
      // Enhanced error handling
      if (error.response) {
        error.statusCode = error.response.status;
        
        // Special handling for validation errors
        if (error.response.status === 400) {
          error.isValidationError = true;
          console.log('Validation error (400 Bad Request)');
        }
      }
      
      console.groupEnd();
      throw error;
    }
  },

  updateContract: async (contractId, contractData) => {
    if (MOCK_ENABLED) {
      return reinsuranceMockService.updateContract(contractId, contractData);
    }
    
    console.group('Updating Reinsurance Contract');
    console.log('Contract ID:', contractId, 'Contract Data:', contractData);
    
    try {
      if (!contractId) {
        throw new Error('Contract ID is required');
      }
      
      const response = await apiService.put(`${API_ENDPOINTS.REINSURANCE.UPDATE_CONTRACT}/${contractId}`, contractData);
      
      console.log('Contract updated successfully:', response);
      console.groupEnd();
      return response;
    } catch (error) {
      console.error('Error updating contract:', error);
      
      // Enhanced error handling
      if (error.response) {
        error.statusCode = error.response.status;
        
        // Special handling for validation errors
        if (error.response.status === 400) {
          error.isValidationError = true;
          console.log('Validation error (400 Bad Request)');
        }
        
        // Special handling for not found
        if (error.response.status === 404) {
          error.isContractNotFoundError = true;
          console.log('Contract not found (404 Not Found)');
        }
      }
      
      console.groupEnd();
      throw error;
    }
  },

  // Verify RNR (Reinsurance Name Record)
  verifyRNR: async (rnr) => {
    if (MOCK_ENABLED) {
      return reinsuranceMockService.verifyRNR(rnr);
    }
    
    console.group('Verifying RNR');
    console.log('RNR:', rnr);
    
    try {
      if (!rnr) {
        throw new Error('RNR is required');
      }
      
      const response = await apiService.post(API_ENDPOINTS.REINSURANCE.VERIFY_RNR, { rnr });
      
      console.log('RNR verified successfully:', response);
      console.groupEnd();
      return response;
    } catch (error) {
      console.error('Error verifying RNR:', error);
      console.groupEnd();
      throw error;
    }
  },

  // Claim Management
  
  // Register a new claim
  registerClaim: async (claimData) => {
    if (MOCK_ENABLED) {
      return reinsuranceMockService.registerClaim(claimData);
    }
    
    console.group('Registering Reinsurance Claim');
    console.log('Claim Data:', claimData);
    
    try {
      const response = await apiService.post(API_ENDPOINTS.REINSURANCE.REGISTER_CLAIM, claimData);
      
      console.log('Claim registered successfully:', response);
      console.groupEnd();
      return response;
    } catch (error) {
      console.error('Error registering claim:', error);
      console.groupEnd();
      throw error;
    }
  },

  // Process a claim
  processClaim: async (claimId, processData) => {
    if (MOCK_ENABLED) {
      return reinsuranceMockService.processClaim(claimId, processData);
    }
    
    console.group('Processing Reinsurance Claim');
    console.log('Claim ID:', claimId, 'Process Data:', processData);
    
    try {
      if (!claimId) {
        throw new Error('Claim ID is required');
      }
      
      const response = await apiService.post(API_ENDPOINTS.REINSURANCE.PROCESS_CLAIM, {
        claim_id: claimId,
        ...processData
      });
      
      console.log('Claim processed successfully:', response);
      console.groupEnd();
      return response;
    } catch (error) {
      console.error('Error processing claim:', error);
      console.groupEnd();
      throw error;
    }
  },

  // Settle a claim
  settleClaim: async (claimId, settlementData) => {
    if (MOCK_ENABLED) {
      return reinsuranceMockService.settleClaim(claimId, settlementData);
    }
    
    console.group('Settling Reinsurance Claim');
    console.log('Claim ID:', claimId, 'Settlement Data:', settlementData);
    
    try {
      if (!claimId) {
        throw new Error('Claim ID is required');
      }
      
      const response = await apiService.post(API_ENDPOINTS.REINSURANCE.SETTLE_CLAIM, {
        claim_id: claimId,
        ...settlementData
      });
      
      console.log('Claim settled successfully:', response);
      console.groupEnd();
      return response;
    } catch (error) {
      console.error('Error settling claim:', error);
      console.groupEnd();
      throw error;
    }
  },

  // Get claims report
  getClaimsReport: async (filters = {}) => {
    if (MOCK_ENABLED) {
      return reinsuranceMockService.getClaimsReport(filters);
    }
    
    console.group('Getting Reinsurance Claims Report');
    console.log('Filters:', filters);
    
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      if (filters.contractId) queryParams.append('contract_id', filters.contractId);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.fromDate) queryParams.append('from_date', filters.fromDate);
      if (filters.toDate) queryParams.append('to_date', filters.toDate);
      
      const endpoint = `${API_ENDPOINTS.REINSURANCE.REPORT_CLAIMS}?${queryParams.toString()}`;
      const response = await apiService.get(endpoint);
      
      console.log('Claims report retrieved successfully:', response);
      console.groupEnd();
      return response;
    } catch (error) {
      console.error('Error getting claims report:', error);
      console.groupEnd();
      throw error;
    }
  },

  // Capital Management
  
  // Get capital position
  getCapitalPosition: async () => {
    if (MOCK_ENABLED) {
      return reinsuranceMockService.getCapitalPosition();
    }
    
    console.group('Getting Capital Position');
    
    try {
      const response = await apiService.get(API_ENDPOINTS.REINSURANCE.GET_CAPITAL_POSITION);
      
      console.log('Capital position retrieved successfully:', response);
      console.groupEnd();
      return response;
    } catch (error) {
      console.error('Error getting capital position:', error);
      console.groupEnd();
      throw error;
    }
  },

  // Allocate capital
  allocateCapital: async (allocationData) => {
    if (MOCK_ENABLED) {
      return reinsuranceMockService.allocateCapital(allocationData);
    }
    
    console.group('Allocating Capital');
    console.log('Allocation Data:', allocationData);
    
    try {
      const response = await apiService.post(API_ENDPOINTS.REINSURANCE.ALLOCATE_CAPITAL, allocationData);
      
      console.log('Capital allocated successfully:', response);
      console.groupEnd();
      return response;
    } catch (error) {
      console.error('Error allocating capital:', error);
      console.groupEnd();
      throw error;
    }
  },

  // Update capital allocation
  updateAllocation: async (allocationId, updateData) => {
    if (MOCK_ENABLED) {
      return reinsuranceMockService.updateAllocation(allocationId, updateData);
    }
    
    console.group('Updating Capital Allocation');
    console.log('Allocation ID:', allocationId, 'Update Data:', updateData);
    
    try {
      if (!allocationId) {
        throw new Error('Allocation ID is required');
      }
      
      const response = await apiService.put(API_ENDPOINTS.REINSURANCE.UPDATE_ALLOCATION, {
        allocation_id: allocationId,
        ...updateData
      });
      
      console.log('Allocation updated successfully:', response);
      console.groupEnd();
      return response;
    } catch (error) {
      console.error('Error updating allocation:', error);
      console.groupEnd();
      throw error;
    }
  },

  // Optimize capital allocation
  optimizeAllocation: async (optimizationParams) => {
    if (MOCK_ENABLED) {
      return reinsuranceMockService.optimizeAllocation(optimizationParams);
    }
    
    console.group('Optimizing Capital Allocation');
    console.log('Optimization Parameters:', optimizationParams);
    
    try {
      const response = await apiService.post(API_ENDPOINTS.REINSURANCE.OPTIMIZE_ALLOCATION, optimizationParams);
      
      console.log('Allocation optimization completed successfully:', response);
      console.groupEnd();
      return response;
    } catch (error) {
      console.error('Error optimizing allocation:', error);
      console.groupEnd();
      throw error;
    }
  },

  // Risk Modeling
  
  // Assess risk
  assessRisk: async (riskData) => {
    if (MOCK_ENABLED) {
      return reinsuranceMockService.assessRisk(riskData);
    }
    
    console.group('Assessing Risk');
    console.log('Risk Data:', riskData);
    
    try {
      const response = await apiService.post(API_ENDPOINTS.REINSURANCE.ASSESS_RISK, riskData);
      
      console.log('Risk assessment completed successfully:', response);
      console.groupEnd();
      return response;
    } catch (error) {
      console.error('Error assessing risk:', error);
      console.groupEnd();
      throw error;
    }
  },

  // Price risk
  priceRisk: async (pricingData) => {
    if (MOCK_ENABLED) {
      return reinsuranceMockService.priceRisk(pricingData);
    }
    
    console.group('Pricing Risk');
    console.log('Pricing Data:', pricingData);
    
    try {
      const response = await apiService.post(API_ENDPOINTS.REINSURANCE.PRICE_RISK, pricingData);
      
      console.log('Risk pricing completed successfully:', response);
      console.groupEnd();
      return response;
    } catch (error) {
      console.error('Error pricing risk:', error);
      console.groupEnd();
      throw error;
    }
  },

  // Model exposure
  modelExposure: async (exposureData) => {
    if (MOCK_ENABLED) {
      return reinsuranceMockService.modelExposure(exposureData);
    }
    
    console.group('Modeling Exposure');
    console.log('Exposure Data:', exposureData);
    
    try {
      const response = await apiService.post(API_ENDPOINTS.REINSURANCE.MODEL_EXPOSURE, exposureData);
      
      console.log('Exposure modeling completed successfully:', response);
      console.groupEnd();
      return response;
    } catch (error) {
      console.error('Error modeling exposure:', error);
      console.groupEnd();
      throw error;
    }
  },

  // Aggregate exposure
  aggregateExposure: async (aggregationParams) => {
    if (MOCK_ENABLED) {
      return reinsuranceMockService.aggregateExposure(aggregationParams);
    }
    
    console.group('Aggregating Exposure');
    console.log('Aggregation Parameters:', aggregationParams);
    
    try {
      const response = await apiService.post(API_ENDPOINTS.REINSURANCE.AGGREGATE_EXPOSURE, aggregationParams);
      
      console.log('Exposure aggregation completed successfully:', response);
      console.groupEnd();
      return response;
    } catch (error) {
      console.error('Error aggregating exposure:', error);
      console.groupEnd();
      throw error;
    }
  },

  // Add this method to reinsuranceService
  getExposureData: async () => {
    if (MOCK_ENABLED) {
      return reinsuranceMockService.getExposureData();
    }
    
    console.group('Getting Exposure Data');
    
    try {
      const response = await apiService.get(API_ENDPOINTS.REINSURANCE.GET_EXPOSURE_DATA);
      
      console.log('Exposure data retrieved successfully:', response);
      console.groupEnd();
      return response;
    } catch (error) {
      console.error('Error getting exposure data:', error);
      console.groupEnd();
      throw error;
    }
  },

  // Reporting
  
  // Generate report
  generateReport: async (reportParams) => {
    if (MOCK_ENABLED) {
      return reinsuranceMockService.generateReport(reportParams);
    }
    
    console.group('Generating Report');
    console.log('Report Parameters:', reportParams);
    
    try {
      const response = await apiService.post(API_ENDPOINTS.REINSURANCE.GENERATE_REPORT, reportParams);
      
      console.log('Report generated successfully:', response);
      console.groupEnd();
      return response;
    } catch (error) {
      console.error('Error generating report:', error);
      console.groupEnd();
      throw error;
    }
  },

  // Get investor reporting
  getInvestorReporting: async (investorId, reportingParams = {}) => {
    if (MOCK_ENABLED) {
      return reinsuranceMockService.getInvestorReporting(investorId, reportingParams);
    }
    
    console.group('Getting Investor Reporting');
    console.log('Investor ID:', investorId, 'Reporting Parameters:', reportingParams);
    
    try {
      if (!investorId) {
        throw new Error('Investor ID is required');
      }
      
      // Build query parameters
      const queryParams = new URLSearchParams();
      queryParams.append('investor_id', investorId);
      if (reportingParams.fromDate) queryParams.append('from_date', reportingParams.fromDate);
      if (reportingParams.toDate) queryParams.append('to_date', reportingParams.toDate);
      if (reportingParams.reportType) queryParams.append('report_type', reportingParams.reportType);
      
      const endpoint = `${API_ENDPOINTS.REINSURANCE.INVESTOR_REPORTING}?${queryParams.toString()}`;
      const response = await apiService.get(endpoint);
      
      console.log('Investor reporting retrieved successfully:', response);
      console.groupEnd();
      return response;
    } catch (error) {
      console.error('Error getting investor reporting:', error);
      console.groupEnd();
      throw error;
    }
  },

  // Get regulatory reporting
  getRegulatoryReporting: async (reportingParams = {}) => {
    if (MOCK_ENABLED) {
      return reinsuranceMockService.getRegulatoryReporting(reportingParams);
    }
    
    console.group('Getting Regulatory Reporting');
    console.log('Reporting Parameters:', reportingParams);
    
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      if (reportingParams.regime) queryParams.append('regime', reportingParams.regime);
      if (reportingParams.fromDate) queryParams.append('from_date', reportingParams.fromDate);
      if (reportingParams.toDate) queryParams.append('to_date', reportingParams.toDate);
      if (reportingParams.reportType) queryParams.append('report_type', reportingParams.reportType);
      
      const endpoint = `${API_ENDPOINTS.REINSURANCE.REGULATORY_REPORTING}?${queryParams.toString()}`;
      const response = await apiService.get(endpoint);
      
      console.log('Regulatory reporting retrieved successfully:', response);
      console.groupEnd();
      return response;
    } catch (error) {
      console.error('Error getting regulatory reporting:', error);
      console.groupEnd();
      throw error;
    }
  },

  // Cedent Management
  listCedents: async (filters = {}) => {
    if (MOCK_ENABLED) {
      return reinsuranceMockService.listCedents(filters);
    }
    
    console.group('Listing Cedents');
    console.log('Filters:', filters);
    
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      if (filters.name) queryParams.append('name', filters.name);
      if (filters.domicile) queryParams.append('domicile', filters.domicile);
      
      const endpoint = `${API_ENDPOINTS.REINSURANCE.LIST_CEDENTS}?${queryParams.toString()}`;
      const response = await apiService.get(endpoint);
      
      console.log('Cedents retrieved successfully:', response);
      console.groupEnd();
      return response;
    } catch (error) {
      console.error('Error listing cedents:', error);
      console.groupEnd();
      throw error;
    }
  },

  getCedent: async (cedentId) => {
    if (MOCK_ENABLED) {
      return reinsuranceMockService.getCedent(cedentId);
    }
    
    console.group('Getting Cedent');
    console.log('Cedent ID:', cedentId);
    
    try {
      if (!cedentId) {
        throw new Error('Cedent ID is required');
      }
      
      const response = await apiService.get(`${API_ENDPOINTS.REINSURANCE.GET_CEDENT}/${cedentId}`);
      
      console.log('Cedent retrieved successfully:', response);
      console.groupEnd();
      return response;
    } catch (error) {
      console.error('Error getting cedent:', error);
      console.groupEnd();
      throw error;
    }
  },

  createCedent: async (cedentData) => {
    if (MOCK_ENABLED) {
      return reinsuranceMockService.createCedent(cedentData);
    }
    
    console.group('Creating Cedent');
    console.log('Cedent Data:', cedentData);
    
    try {
      const response = await apiService.post(API_ENDPOINTS.REINSURANCE.CREATE_CEDENT, cedentData);
      
      console.log('Cedent created successfully:', response);
      console.groupEnd();
      return response;
    } catch (error) {
      console.error('Error creating cedent:', error);
      console.groupEnd();
      throw error;
    }
  },

  updateCedent: async (cedentId, cedentData) => {
    if (MOCK_ENABLED) {
      return reinsuranceMockService.updateCedent(cedentId, cedentData);
    }
    
    console.group('Updating Cedent');
    console.log('Cedent ID:', cedentId, 'Cedent Data:', cedentData);
    
    try {
      if (!cedentId) {
        throw new Error('Cedent ID is required');
      }
      
      const response = await apiService.put(`${API_ENDPOINTS.REINSURANCE.UPDATE_CEDENT}/${cedentId}`, cedentData);
      
      console.log('Cedent updated successfully:', response);
      console.groupEnd();
      return response;
    } catch (error) {
      console.error('Error updating cedent:', error);
      console.groupEnd();
      throw error;
    }
  },

  // Get cedent performance
  getCedentPerformance: async (cedentId) => {
    if (MOCK_ENABLED) {
      return reinsuranceMockService.getCedentPerformance(cedentId);
    }
    
    console.group('Getting Cedent Performance');
    console.log('Cedent ID:', cedentId);
    
    try {
      if (!cedentId) {
        throw new Error('Cedent ID is required');
      }
      
      const response = await apiService.get(`${API_ENDPOINTS.REINSURANCE.CEDENT_PERFORMANCE}/${cedentId}`);
      
      console.log('Cedent performance retrieved successfully:', response);
      console.groupEnd();
      return response;
    } catch (error) {
      console.error('Error getting cedent performance:', error);
      console.groupEnd();
      throw error;
    }
  },

  // Helper methods - these don't need to be mocked as they're client-side only
  
  // Format currency for display
  formatCurrency: (amount, currency = 'USD') => {
    if (amount === undefined || amount === null) return '';
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  },
  
  // Format percentage for display
  formatPercentage: (value) => {
    if (value === undefined || value === null) return '';
    
    return `${value}%`;
  },
  
  // Format date for display
  formatDate: (dateString) => {
    if (!dateString) return '';
    
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  },
  
  // Check if a date is in the past
  isExpired: (dateString) => {
    if (!dateString) return false;
    
    const date = new Date(dateString);
    const now = new Date();
    
    return date < now;
  },
  
  // Get contract type options
  getContractTypeOptions: () => {
    return [
      { value: 'property', label: 'Property' },
      { value: 'specialty', label: 'Specialty' },
      { value: 'ilw', label: 'ILW' },
      { value: 'parametric', label: 'Parametric' },
      { value: 'facultative', label: 'Facultative' }
    ];
  },
  
  // Get contract structure options
  getContractStructureOptions: () => {
    return [
      { value: 'proportional', label: 'Proportional' },
      { value: 'non_proportional', label: 'Non-Proportional' },
      { value: 'hybrid', label: 'Hybrid' }
    ];
  },
  
  // Get contract status options
  getContractStatusOptions: () => {
    return [
      { value: 'draft', label: 'Draft' },
      { value: 'quoted', label: 'Quoted' },
      { value: 'bound', label: 'Bound' },
      { value: 'active', label: 'Active' },
      { value: 'expired', label: 'Expired' },
      { value: 'cancelled', label: 'Cancelled' },
      { value: 'commuted', label: 'Commuted' }
    ];
  }
};

export default reinsuranceService;